/**
 * PostHog 事件 schema 集中管理（T11 §12.3 + §12.5）
 *
 * 承接：
 * - T11 §12.3 Admin 9 events 完整字段定义
 * - T11 §12.4 Widget 8 events（S2+ 接 widget 时启用 · 暂留占位 type 不导出 Zod）
 * - T11 §12.5 实现约束：CI 检查未登记的事件名不允许发送 · 通过 Zod discriminatedUnion + track() 包装实现
 * - P6 §3.2.3 PII 不入埋点（仅 metadata · 长度 / 轮数 / 延迟）
 *
 * 架构：
 * - Zod discriminatedUnion("event") 把 9 events 串成单一类型 · 任一字段拼写错则编译期失败
 * - track() 包装 posthog.capture · 接收 z.infer<typeof AdminEvent> · 类型安全
 * - 未登记事件名（不在 union 中）→ TypeScript 直接 reject
 */
import { z } from "zod";

// ─── Admin 9 events（T11 §12.3）───

export const AdminEvent = z.discriminatedUnion("event", [
	z.object({
		event: z.literal("admin_page_view"),
		properties: z.object({
			mxx_id: z.string().min(1).describe("M01-M14 / global"),
			url: z.string(),
			duration_sec: z.number().nonnegative(),
		}),
	}),
	z.object({
		event: z.literal("admin_eval_job_created"),
		properties: z.object({
			test_suite_id: z.string(),
			persona_count: z.number().int().nonnegative(),
			case_count: z.number().int().nonnegative(),
		}),
	}),
	z.object({
		event: z.literal("admin_judge_prompt_published"),
		properties: z.object({
			dimension: z.enum(["D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8", "D9", "D10"]),
			version: z.string(),
			publish_reason_len: z.number().int().nonnegative(),
		}),
	}),
	z.object({
		event: z.literal("admin_playbook_published"),
		properties: z.object({
			playbook_id: z.string(),
			version: z.string(),
		}),
	}),
	z.object({
		event: z.literal("admin_skill_toggled"),
		properties: z.object({
			skill_id: z.string(),
			enabled: z.boolean(),
		}),
	}),
	z.object({
		event: z.literal("admin_badcase_marked"),
		properties: z.object({
			trace_id: z.string(),
			severity: z.enum(["low", "medium", "high"]),
			root_cause: z.string(),
		}),
	}),
	z.object({
		event: z.literal("admin_knowledge_uploaded"),
		properties: z.object({
			doc_count: z.number().int().nonnegative(),
			total_size_bytes: z.number().int().nonnegative(),
		}),
	}),
	z.object({
		event: z.literal("admin_search_used"),
		properties: z.object({
			query_length: z.number().int().nonnegative(),
			result_count: z.number().int().nonnegative(),
		}),
	}),
	z.object({
		event: z.literal("admin_command_palette_opened"),
		properties: z.object({}).strict(),
	}),
]);

export type AdminEventInput = z.infer<typeof AdminEvent>;

/** 9 admin event names · 用于 CI 检查 / 反射 */
export const ADMIN_EVENT_NAMES = [
	"admin_page_view",
	"admin_eval_job_created",
	"admin_judge_prompt_published",
	"admin_playbook_published",
	"admin_skill_toggled",
	"admin_badcase_marked",
	"admin_knowledge_uploaded",
	"admin_search_used",
	"admin_command_palette_opened",
] as const;

export type AdminEventName = (typeof ADMIN_EVENT_NAMES)[number];
